import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";

/**
 * Minimal wrapper around leaflet.markercluster for react-leaflet v5.
 * Renders a <MarkerClusterGroup>{markers}</MarkerClusterGroup>-style block by
 * imperatively adding a Leaflet cluster layer and swapping its markers when
 * the `markers` prop changes.
 *
 * @param {Array<{ position:[number,number], icon:L.DivIcon, popupHtml?:string, onOpen?:()=>void }>} markers
 */
export default function MarkerClusterGroup({ markers = [], options = {} }) {
  const map = useMap();
  const clusterRef = useRef(null);

  useEffect(() => {
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 17,
      maxClusterRadius: 55,
      chunkedLoading: true,
      ...options,
    });
    clusterRef.current = cluster;
    map.addLayer(cluster);
    return () => {
      map.removeLayer(cluster);
      clusterRef.current = null;
    };
    // eslint-disable-next-line
  }, [map]);

  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    markers.forEach((m) => {
      const marker = L.marker(m.position, { icon: m.icon });
      if (m.popupHtml) {
        marker.bindPopup(m.popupHtml, { closeButton: true, autoClose: true });
      }
      if (m.onClick) marker.on("click", m.onClick);
      if (m.onPopupOpen) marker.on("popupopen", m.onPopupOpen);
      cluster.addLayer(marker);
    });
  }, [markers]);

  return null;
}
